import React, { useEffect, useRef, useState } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import { getService } from '@Client/runner.hooks/useService';
import { Guid } from '@Shared.Angular/@types/guid';
import ITagDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagDetail';
import { TagGroupValidationErrorType } from '@Shared.Angular/@types/core/contracts/constants/tagGroupValidationErrorType';
import { ITagGroupValidationError } from '@Shared.Angular/@types/core/contracts/exceptions/tagGroupValidationError';
import { TagColorGradient } from '../../TagColorGradient/TagColorGradient';
import TagSelector from '@Client/runner.setup/runner.setup.taggroups/components/TagSelector/TagSelector';

export type Props = {
  id: Guid;
  title: string;
  name: string;
  description: string;
  color: string;
  tags: ITagDetail[];
  saveSuccess: () => void;
  closeModal: () => void;
};

const defaultColors = [
  'E15F45',
  '6D9BD0',
  '856CAA',
  'D298A5',
  '55B1A5',
  'A1C197',
  'E0B346',
  'E98066',
  '49A270',
  '516B75',
  '37769B',
  'B66578',
  'DA9254',
  '819A79',
  'F06F72',
  '63A693'
];
const MAX_TAG_GROUP_NAME_LENGTH = 100;
const MAX_TAG_GROUP_DESCRIPTION_LENGTH = 300;

export const TagGroupEditModal = ({
  id,
  title,
  name,
  description,
  color,
  tags,
  saveSuccess,
  closeModal
}: Props) => {
  const [nameInput, setNameInput] = useState(name);
  const [descriptionInput, setDescriptionInput] = useState(description);
  const [colorInput, setColorInput] = useState(color || defaultColors[0]);
  const [tagsInput, setTagsInput] = useState<ITagDetail[]>(tags);
  const [hasNameError, setHasNameError] = useState(false);
  const [tagError, setTagError] = useState('');
  const [tagGroupNameError, setTagGroupNameError] = useState('');
  const [isColorInvalid, setIsColorInvalid] = useState(false);
  const [errorMessagesOnSubmit, setErrorMessagesOnSubmit] = useState<string[]>(
    []
  );
  const [nameFocus, setNameFocus] = useState(false);
  const [descriptionFocus, setDescriptionFocus] = useState(false);
  const [colors, setColors] = useState(defaultColors);

  const notificationService = getService('notificationService');
  const appInsightsService = getService('appInsightsService');
  const tagGroupApiService = getService('tagGroupApiService');
  const tagService = getService('tagService');

  const originalTagIndexMap = useRef<
    Map<Guid, { name: string; index: number }>
  >(new Map());

  useEffect(() => {
    const tagIndexMap = new Map<Guid, { name: string; index: number }>();
    tags.forEach((tag, index) =>
      tagIndexMap.set(tag.id, { name: tag.name, index })
    );
    originalTagIndexMap.current = tagIndexMap;
  }, [tags]);

  useEffect(() => {
    refreshColors(colorInput);
  }, [color, colorInput]);

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setNameInput(value);

    if (value) {
      setHasNameError(false);

      if (!tagService.isValidTagName(value.trim())) {
        setTagGroupNameError(
          'Invalid characters detected in tag group name. Only letters, numbers, spaces, periods, hyphens, parentheses, and underscores are allowed.'
        );
      } else {
        setTagGroupNameError('');
      }
    } else {
      setHasNameError(true);
      setTagGroupNameError('');
    }
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setDescriptionInput(event.target.value);
  };

  const save = async (): Promise<void> => {
    if (!nameInput) {
      setHasNameError(true);
      return;
    }
    if (tagGroupNameError) {
      return;
    }

    appInsightsService.startEventTimer('setupTaggroupsSaved');

    try {
      await tagGroupApiService.save({
        id,
        name: nameInput.trim(),
        description: descriptionInput.trim(),
        color: colorInput,
        tags: tagsInput
      });

      saveSuccess();
      notificationService.showSuccessToast('Tag Group saved successfully');
    } catch (error) {
      handleError(error);
    }
  };

  const handleError = (error: {
    data: { errors: ITagGroupValidationError[] };
  }): void => {
    const tagsToRestore: { id: Guid; name: string; index: number }[] = [];

    error.data.errors.forEach((error: ITagGroupValidationError) => {
      if (error.type === TagGroupValidationErrorType.UsedTagsCannotBeDeleted) {
        const id = error.usedTagId;
        const info = originalTagIndexMap.current.get(id);
        if (info) {
          tagsToRestore.push({ id, name: info.name, index: info.index });
        }
      }
    });

    if (tagsToRestore.length > 0) {
      tagsToRestore.sort((a, b) => a.index - b.index);
      setTagsInput((prev) => {
        const newTags = [...prev];
        tagsToRestore.forEach((tag) => {
          if (!newTags.some((t) => t.id === tag.id)) {
            newTags.splice(tag.index, 0, { id: tag.id, name: tag.name });
          }
        });
        return newTags;
      });
    }

    const usedTagErrors = error.data.errors.filter(
      (e: ITagGroupValidationError) =>
        e.type === TagGroupValidationErrorType.UsedTagsCannotBeDeleted
    );

    const otherErrors = error.data.errors.filter(
      (e: ITagGroupValidationError) =>
        e.type !== TagGroupValidationErrorType.UsedTagsCannotBeDeleted
    );

    usedTagErrors.sort(
      (a: ITagGroupValidationError, b: ITagGroupValidationError) => {
        const aIndex = originalTagIndexMap.current.get(a.usedTagId)?.index ?? 0;
        const bIndex = originalTagIndexMap.current.get(b.usedTagId)?.index ?? 0;
        return aIndex - bIndex;
      }
    );

    setErrorMessagesOnSubmit([
      ...otherErrors.map((e: ITagGroupValidationError) => e.message),
      ...usedTagErrors.map((e: ITagGroupValidationError) => e.message)
    ]);
  };

  const refreshColors = (color: string): void => {
    setColors((prevColors) => {
      const updatedColors = [...prevColors];
      if (
        defaultColors.includes(color) &&
        updatedColors.length > defaultColors.length
      ) {
        updatedColors.pop();
      }
      if (!updatedColors.includes(color)) {
        if (updatedColors.length > defaultColors.length) {
          updatedColors.pop();
        }
        updatedColors.push(color);
      }
      return updatedColors;
    });

    setColorInput(color);
  };

  return (
    <Modal
      id="tag-group-edit-modal"
      title={title}
      onClose={closeModal}
      onCancelClick={closeModal}
      onActionClick={save}
      isActionDisabled={isColorInvalid}
      actionButtonText="Save"
      actionButtonType="primary"
      size="md"
    >
      <div className="tag-group-edit-modal">
        <div className="input-field">
          <label htmlFor="category-name">Group Name *</label>
          <input
            id="category-name"
            type="text"
            value={nameInput}
            maxLength={MAX_TAG_GROUP_NAME_LENGTH}
            onChange={handleNameChange}
            onFocus={() => setNameFocus(true)}
            onBlur={() => setNameFocus(false)}
            data-testid="tag-group-name-input"
            required
          />
          {hasNameError && <div className="error-msg">Please enter a name</div>}
          {nameFocus && (
            <div className="char-counter error-msg">
              Number of characters left:{' '}
              {MAX_TAG_GROUP_NAME_LENGTH - nameInput.length}
            </div>
          )}
        </div>

        <div className="input-field">
          <label htmlFor="category-description">Description</label>
          <input
            id="category-description"
            type="text"
            value={descriptionInput}
            maxLength={MAX_TAG_GROUP_DESCRIPTION_LENGTH}
            onChange={handleDescriptionChange}
            onFocus={() => setDescriptionFocus(true)}
            onBlur={() => setDescriptionFocus(false)}
            data-testid="tag-group-description-input"
          />
          {descriptionFocus && (
            <div className="char-counter error-text">
              Number of characters left:{' '}
              {MAX_TAG_GROUP_DESCRIPTION_LENGTH - descriptionInput.length}
            </div>
          )}
        </div>

        <div className="input-field">
          <label>Color</label>
          <TagColorGradient
            colors={colors}
            selectedColor={colorInput}
            onSelectColor={setColorInput}
            onValidityChange={(isValid) => setIsColorInvalid(!isValid)}
          />
        </div>

        <div className="input-field">
          <label>Tags</label>
          <TagSelector
            tags={tagsInput}
            color={colorInput}
            onAddTag={(newTag) => setTagsInput([...tagsInput, newTag])}
            onRemoveTag={(name) =>
              setTagsInput(tagsInput.filter((tag) => tag.name !== name))
            }
            onUpdateTag={({ name, updatedName }) => {
              setTagsInput(
                tagsInput.map((tag) =>
                  tag.name === name ? { ...tag, name: updatedName } : tag
                )
              );
            }}
            onRemoveAllTags={() => setTagsInput([])}
            onTagNameValidationError={(message) => setTagError(message)}
          />
        </div>
        {errorMessagesOnSubmit.length > 0 && (
          <div className="tag-group-error validation-errors">
            <ul>
              {errorMessagesOnSubmit.map((msg, idx) => (
                <li key={idx} className="error-message-on-submit">
                  {msg}
                </li>
              ))}
            </ul>
          </div>
        )}

        {tagError && (
          <div className="tag-group-error validation-errors">
            <p>{tagError}</p>
          </div>
        )}

        {tagGroupNameError && (
          <div className="tag-group-error validation-errors">
            <p>{tagGroupNameError}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};
